import { ChangeEvent, FC, useEffect } from 'react';
import { Col, Popover, Radio, RadioChangeEvent, Row } from 'antd';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
    clearOnboardingErrorsValidation,
    clearOnboardingWorkPermit,
    updateOnboardingWorkPermit,
} from '../../../../redux/reducers/onboarding/sliceReducer';
import moment from '../../../../common/constants/moment';
import { commonYearDateFormat } from '../../../../common/constants/dateConstants';
import { OnboardingFileKeys } from '../../../../common/api/services/HistoryApi/common/OnboardingFileKeys';
import { ROUTER_PATHS } from '../../../../navigation/routerPaths';
import { historyApi } from '../../../../common/api/services/HistoryApi/historyApi';
import { useParams } from 'react-router-dom';
import { setActiveFields } from '../../../../redux/reducers/onboarding-history/sliceReducer';
import HistoryComponent from '../../history/HistoryComponent';
import { FieldKeys } from '../../../../common/api/services/HistoryApi/types/fieldKeys';
import { initialHistoryFields } from '../../constants/initialHistoryFields';
import OnboardingWorkPermitMessage from '../../../../components/info-messages/OnboardingWorkPermitMessage';
import WarningIcon from '../../../../assets/icons/WarningIcon';
import { Roles } from '../../../../common/enums/Roles';

const WorkPermitTab: FC = () => {
    const dispatch = useAppDispatch();
    const { role } = useAppSelector((state) => state.auth);
    const isAdmin = Roles.ADMIN === role || Roles.MANAGER === role;

    const { consultantId } = useParams<{
        consultantId: string;
    }>();

    const { workPermit } = useAppSelector((state) => state.onboarding);

    const { isReligious, isGainIntermediate } = workPermit;

    useEffect(() => {
        const matchedKey = OnboardingFileKeys.find((item) =>
            Object.keys(item).some((key) => key === ROUTER_PATHS.workPermit)
        );
        if (matchedKey) {
            historyApi
                .getOnboardingActiveFields({
                    userId: consultantId || 'consultant',
                    keys: matchedKey[ROUTER_PATHS.workPermit],
                })
                .then((resp) => dispatch(setActiveFields(resp.data)));
        }
    }, []);

    const { documents, ...restWorkPermit } = useAppSelector((state) => state.onboarding.workPermit);
    const isSwissNationality = useAppSelector((state) => state.onboarding.personalInfo.nationality === 'Switzerland');

    const errorMessages = useAppSelector((state) => state.onboarding.validationError.workPermit);

    const onChange = (event: ChangeEvent<HTMLTextAreaElement> | RadioChangeEvent): void => {
        const fieldName = event.target.name!;
        const fieldValue = event.target.value;

        if (fieldName === 'hasWorkPermit' && !fieldValue) {
            dispatch(clearOnboardingWorkPermit());
            dispatch(clearOnboardingErrorsValidation());
        }

        dispatch(updateOnboardingWorkPermit({ [fieldName]: fieldValue }));
    };

    const onChangeDate =
        (fieldName: string) =>
        (date: Date | null): void => {
            const value = date ? moment(date)?.format(commonYearDateFormat) : '';
            dispatch(updateOnboardingWorkPermit({ [fieldName]: value }));
        };

    const onChangeDropdown =
        (fieldName: string) =>
        (data: any): void => {
            dispatch(updateOnboardingWorkPermit({ [fieldName]: data }));
        };

    const getError = (fieldName: string): string => {
        if (errorMessages) {
            return errorMessages[fieldName] ?? '';
        }
        return '';
    };

    return (
        <div className="bank-account-v2" style={{ overflowY: 'auto' }}>
            {isSwissNationality && <OnboardingWorkPermitMessage />}
            <div className="bank-account-title">
                <h4>Work permit</h4>
            </div>
            <HistoryComponent
                fieldKey={FieldKeys.workPermit}
                tabName="Work permit"
                onChangeArray={[]}
                shownElements={initialHistoryFields[FieldKeys.workPermit]}
                isHideTabName
                disableCondition={isSwissNationality}
            />
            <Row>
                <Col span={12}>
                    <div className="address-title">
                        <h4>Religion</h4>
                        <Popover
                            className="info-pop-button"
                            placement="topRight"
                            content={() => (
                                <span>
                                    As you are subject to source tax, the authorities ask whether you are religious. If
                                    this is the case, you will be subject to church tax.
                                </span>
                            )}
                            trigger="hover"
                            overlayClassName="info-pop-content"
                            showArrow={false}
                        >
                            <button>
                                <WarningIcon />
                            </button>
                        </Popover>
                    </div>
                    <Row justify="start" style={{ padding: '0 5px' }}>
                        <Col span={24}>
                            <Row
                                justify="space-between"
                                align="middle"
                                gutter={[16, 16]}
                                style={{
                                    height: 54,
                                    backgroundColor: '#f5f7fa',
                                    borderRadius: '12px',
                                    padding: '0 15px',
                                    // width: '98%',
                                }}
                            >
                                <Col span={17}>
                                    <span>Religion</span>
                                </Col>
                                <Col>
                                    <Radio.Group name="isReligious" value={isReligious} onChange={onChange}>
                                        <Radio value={true}>Yes</Radio>
                                        <Radio value={false}>No</Radio>
                                    </Radio.Group>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {getError('data.isReligious') && (
                        <span className="error-message error-text">{getError('data.isReligious')}</span>
                    )}
                </Col>
                {isAdmin && (
                    <Col span={12}>
                        <div className="address-title">
                            <h4>Gain intermédiaire</h4>
                        </div>
                        <Row justify="start" style={{ padding: '0 5px' }}>
                            <Col span={24}>
                                <Row
                                    justify="space-between"
                                    align="middle"
                                    gutter={[16, 16]}
                                    style={{
                                        height: 54,
                                        backgroundColor: '#f5f7fa',
                                        borderRadius: '12px',
                                        padding: '0 15px',
                                        // width: '98%',
                                    }}
                                >
                                    <Col span={17}>
                                        <span>Gain intermédiaire</span>
                                    </Col>
                                    <Col>
                                        <Radio.Group
                                            name="isGainIntermediate"
                                            value={isGainIntermediate}
                                            onChange={onChange}
                                        >
                                            <Radio value={true}>Yes</Radio>
                                            <Radio value={false}>No</Radio>
                                        </Radio.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        {getError('data.isGainIntermediate') && (
                            <span className="error-message error-text">{getError('data.isGainIntermediate')}</span>
                        )}
                    </Col>
                )}
            </Row>
        </div>
    );
};

export default WorkPermitTab;
